/**
 * RentalEquipmentPicker
 * Two-panel component: selected list (left) + search panel (right)
 * Used by PackageScopeSection when linkingMode === 'EQUIPMENT'
 */

import { getProducts, type Product } from '@/api/products';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

export interface PickedEquipment {
  productId: string;
  name: string;
  sku: string;
}

interface Props {
  selected: PickedEquipment[];
  onChange: (next: PickedEquipment[]) => void;
}

export function RentalEquipmentPicker({ selected, onChange }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await getProducts({
        search,
        pageSize: 20,
        type: 'RENTAL_EQUIPMENT',
      });
      setResults(response.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => void handleSearch(), 300);
    return () => clearTimeout(timer);
  }, [handleSearch]);

  const add = (product: Product) => {
    if (selected.some(s => s.productId === product.id)) return;
    onChange([...selected, { productId: product.id, name: product.name, sku: product.sku }]);
  };

  const remove = (productId: string) => {
    onChange(selected.filter(s => s.productId !== productId));
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Selected panel */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            {t('packages.scope.equipment.selectedTitle', { count: selected.length })}
          </h3>
          {selected.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t('packages.scope.equipment.selectedEmpty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {selected.map(item => (
                <li
                  key={item.productId}
                  className="flex items-center justify-between rounded-md border p-2 dark:border-slate-700"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.sku}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600"
                    onClick={() => remove(item.productId)}
                    aria-label={t('packages.scope.equipment.remove')}
                  >
                    ×
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Search panel */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            {t('packages.scope.equipment.searchTitle')}
          </h3>
          <Input
            placeholder={t('packages.scope.equipment.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="mb-3"
          />
          {loading && (
            <p className="py-2 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
          )}
          {!loading && search && results.length === 0 && (
            <p className="py-2 text-center text-sm text-muted-foreground">
              {t('packages.scope.equipment.searchEmpty')}
            </p>
          )}
          <ul className="max-h-[400px] space-y-2 overflow-y-auto">
            {results.map(product => {
              const added = selected.some(s => s.productId === product.id);
              return (
                <li
                  key={product.id}
                  className={`flex items-center justify-between rounded-md border p-2 dark:border-slate-700 ${
                    added
                      ? 'opacity-50'
                      : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50'
                  }`}
                  onClick={() => !added && add(product)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{product.name}</p>
                    <p className="text-xs text-muted-foreground">{product.sku}</p>
                  </div>
                  {added ? (
                    <span className="text-xs text-muted-foreground">
                      {t('packages.scope.equipment.alreadyAdded')}
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm">
                      + {t('packages.scope.equipment.add')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
